/**
 * Copia de kiosk-stand/src/utils/packs-utils.js. As duas apps nao partilham codigo; qualquer
 * alteracao aqui tem de ser replicada la (e vice-versa).
 *
 * NAO confundir com o PACKS_CONFIGS de pages/safety-packs/SafetyPacksPage.js: esse tem outra forma
 * (colorClass em vez de excessColor) e serve a pagina legacy, que fica intocada.
 */

/**
 * GKIOSK-1889 - vertentes da pagina de packs nova. Tem de coincidir com NEW_PACKS_VARIANTS do
 * backend (web-api/src/reservation/new-packs-eligibility.ts), que e quem decide a vertente e a
 * envia no payload da reserva (reservation.newSafetyPacksVariant).
 */
export const PACK_VARIANTS = {
  INCLUSIVE: 'INCLUSIVE',
  EXCLUSIVE: 'EXCLUSIVE'
};

/**
 * GKIOSK-1889 - o Pack Basic e um extra RentWay (PE) e NAO uma linha de SAFETY_PACK: se fosse um
 * pack, o getAuth1Value dava-lhe -50% de caucao (o design diz "Caucao total"), o
 * canShowSafetyPacks/canGoInsurancePacksPage passariam a saltar a pagina em qualquer tarifa
 * inclusive (onde o PE vem com extraIncluded='true') e o filtro de subsuncao de getBestPackForExtras
 * casaria com todas as reservas (['TP','CDW'] menos os ALWAYS_PRESENT e [], e [].every() e true).
 */
export const PACK_BASIC_EXTRA_ID = 'PE';

// O que o Pack Basic cobre, para efeitos das linhas do cartao: roubo (TP) + terceiros (CDW).
export const PACK_BASIC_EXTRAS = ['TP', 'CDW'];

export const packsConfigurations = {
  RF1: { excessColor: '#F2C94C', discounts: { ACDW1: 30 } },
  RF2: { excessColor: '#F2C94C', discounts: { ACDW1: 30, ASTOP: 30 } },
  IF1: { excessColor: '#95C11F', discounts: { ACDW1: 50 } },
  IF2: { excessColor: '#95C11F', highlight: true, showNoDiscountValue: true, discounts: { ACDW1: 50, ASTOP: 50 } },
  IF3: { excessColor: '#95C11F', discounts: { ACDW1: 50, ASTOP: 50, CBF: 50 } },

  // GKIOSK-1888 / GKIOSK-1889 - packs das paginas novas de packs (/insurance-v3 no stand, /safety-packs-v3 no mobile).
  // `variant` marca a vertente a que o pack pertence e `order` a ordem esquerda->direita dos
  // cartoes: estes packs NAO dependem do conjunto configurado no flow (SAFETY_PACK_GROUP_ID), que
  // continua a servir apenas a pagina legacy. Basta a reserva ser elegivel.
  // Atencao: cada percentagem em `discounts` precisa do PNG correspondente em
  // src/assets/manage-extras-icons/ (existem apenas disc-30.png e disc-50.png).
  ESSI: { excessColor: '#F2C94C', variant: PACK_VARIANTS.INCLUSIVE, order: 1, stars: 1, discounts: {} },
  PPI: {
    excessColor: '#95C11F',
    variant: PACK_VARIANTS.INCLUSIVE,
    order: 2,
    stars: 2,
    discountBadge: 30,
    // Fundo cinza no cartao (SVG: #444F50 a 5%). E um destaque fixo deste pack, nao um estado.
    emphasized: true,
    showNoDiscountValue: true,
    discounts: { ACDW1: 50, ASTOP: 50 }
  },
  SPI: {
    excessColor: '#95C11F',
    variant: PACK_VARIANTS.INCLUSIVE,
    order: 3,
    stars: 3,
    discountBadge: 40,
    showNoDiscountValue: true,
    recommended: true,
    discounts: { ACDW1: 50, ASTOP: 50, CBF: 50 }
  },

  // GKIOSK-1889 - vertente exclusive: os mesmos packs com o Pack Basic (PE) incluido. O conteudo e
  // a apresentacao sao identicos aos inclusive; o que muda e o codigo RentWay e o preco. O cartao do
  // Pack Basic nao esta aqui - e sintetizado a partir do extra PE (ver useSafetyPacksPage).
  ESSE: { excessColor: '#F2C94C', variant: PACK_VARIANTS.EXCLUSIVE, order: 1, stars: 1, discounts: {} },
  PPE: {
    excessColor: '#95C11F',
    variant: PACK_VARIANTS.EXCLUSIVE,
    order: 2,
    stars: 2,
    discountBadge: 30,
    emphasized: true,
    showNoDiscountValue: true,
    discounts: { ACDW1: 50, ASTOP: 50 }
  },
  SPE: {
    excessColor: '#95C11F',
    variant: PACK_VARIANTS.EXCLUSIVE,
    order: 3,
    stars: 3,
    discountBadge: 40,
    showNoDiscountValue: true,
    recommended: true,
    discounts: { ACDW1: 50, ASTOP: 50, CBF: 50 }
  }
};

// Staging tem o pack Essencial como ESS; prod como ESSI. Alias num unico ponto.
const PACK_ID_ALIASES = { ESS: 'ESSI' };

export function getPackConfig(extraId) {
  if (!extraId) return undefined;
  return packsConfigurations[extraId] || packsConfigurations[PACK_ID_ALIASES[extraId]];
}

/**
 * GKIOSK-1888 / GKIOSK-1889 - packs da vertente pedida, retirados de TODOS os packs devolvidos pelo
 * backend (reservation.packs, ou seja a tabela SAFETY_PACK completa) e nao do conjunto associado ao
 * flow. O conjunto do flow (SAFETY_PACK_GROUP_ID) continua a servir apenas a pagina legacy.
 */
export function getNewPacks(allPacks, variant) {
  if (!variant) return [];

  return (allPacks || [])
    .filter(p => getPackConfig(p.extraId)?.variant === variant)
    .map(p => ({ ...p, order: getPackConfig(p.extraId).order }))
    .sort((a, b) => a.order - b.order);
}

// Linha "Seguro Responsabilidade Civil": extra obrigatorio, sem codigo RentWay.
export const ALWAYS_ACTIVE_ROWS = ['RC'];

/**
 * GKIOSK-1888 - descritor das 9 linhas do cartao dos packs inclusive.
 * `RC` e `DEP` sao pseudo-linhas (sem codigo de produto RentWay).
 * A linha da franquia muda de label conforme o pack traga RF* (reducao) ou IF* (sem franquia).
 */
export const NEW_PACK_ROWS = [
  { key: 'RC', labelKey: 'packRowCivilLiability', alwaysActive: true },
  { key: 'TP', labelKey: 'packRowTheftCover', extras: ['TP'] },
  { key: 'CDW', labelKey: 'packRowThirdPartyCover', extras: ['CDW'] },
  {
    key: 'FRAN',
    labelKey: 'packRowExcessReduction',
    extras: ['RF', 'RF1', 'RF2'],
    altLabelKey: 'packRowNoExcess',
    altExtras: ['IF', 'IF1', 'IF2', 'IF3']
  },
  { key: 'DEP', labelKey: 'packRowDepositReduction', depositDiscount: true },
  { key: 'ACDW1', labelKey: 'packRowGlassAndWheels', extras: ['ACDW1'] },
  { key: 'ASTOP', labelKey: 'packRowRoadAssistanceTop', extras: ['ASTOP'] },
  { key: 'CBF', labelKey: 'packRowCrossBorder', extras: ['CBF'] },
  { key: 'PACKH', labelKey: 'packRowPackHours', extras: ['PACKH'] }
];

export function formatPackPrice(number) {
  if (!number || isNaN(Number(number))) return '0,00';
  return Number(number).toFixed(2);
}

// GKIOSK-1888 - a percentagem de desconto da caucao esta fixada em getAuth1Value (metade do valor).
export const DEPOSIT_DISCOUNT_PERCENTAGE = 50;

export function depositLabel(pack, t) {
  return pack.auth1 && pack.auth1.withoutDiscountValue
    ? t('depositDiscountLabel', { percentage: DEPOSIT_DISCOUNT_PERCENTAGE })
    : t('depositTotal');
}

/**
 * Explicacao da caucao. Substitui o hint errado que existia (era a explicacao da franquia).
 * Os valores vem de getAuth1Value, ou seja de groupsConfigurations[groupId].safetyDeposit - nada
 * hardcoded.
 */
export function depositHint(pack, t) {
  if (!pack.auth1 || !pack.auth1.isEnabled) {
    return t('depositExplanationNoDiscount', { value: formatPackPrice(0) });
  }
  return pack.auth1.withoutDiscountValue
    ? t('depositExplanation', {
        withoutDiscountValue: formatPackPrice(pack.auth1.withoutDiscountValue),
        value: formatPackPrice(pack.auth1.value)
      })
    : t('depositExplanationNoDiscount', { value: formatPackPrice(pack.auth1.value) });
}

/**
 * Constroi as linhas de um cartao da pagina de packs nova.
 *
 * A ordem vem sempre de NEW_PACK_ROWS e nao de SAFETY_PACK_GROUP.EXTRAS_LABELS: essa coluna e
 * partilhada com a pagina legacy (/insurance), que a usa para renderizar icones e nomes de produto
 * RentWay - as pseudo-linhas RC/FRAN/DEP nao existem nesse contexto.
 *
 * `productId` e o extra RentWay que representa a linha (alimenta icones/video/descricao no modal);
 * as pseudo-linhas (RC, Reducao Caucao) nao tem produto e sao apenas informativas.
 */
export function buildPackRows(pack, t) {
  const packExtras = (pack && pack.extrasList) || [];

  return NEW_PACK_ROWS.map(row => {
    const useAlt = Boolean(row.altExtras && row.altExtras.some(e => packExtras.includes(e)));
    const candidates = (useAlt ? row.altExtras : row.extras) || [];
    const labelKey = useAlt ? row.altLabelKey : row.labelKey;

    const active = row.alwaysActive
      ? true
      : row.depositDiscount
      ? Boolean(pack && pack.auth1 && pack.auth1.withoutDiscountValue)
      : candidates.some(e => packExtras.includes(e));

    // Numa linha inativa nao ha extra presente no pack: usa-se o primeiro candidato para que o
    // modal/tooltip continue a ter copy e icones coerentes.
    const productId = candidates.length > 0 ? candidates.find(e => packExtras.includes(e)) || candidates[0] : null;

    return {
      id: row.key,
      productId,
      labelKey,
      label: t(labelKey),
      active
    };
  });
}

/**
 * Copy de uma linha de pack, partilhada pelo tooltip do cartao (InsurancePageV3) e pelo detalhe do
 * InsurancePackModal, para que a mesma linha diga o mesmo nos dois sitios.
 *
 * As linhas com produto RentWay usam a descricao do proprio extra (`<extraId>Description`, as chaves
 * que o modal legacy V1/V2 ja usa em producao); as pseudo-linhas (RC, Reducao Caucao) nao tem extra e
 * usam a chave do seu label.
 */
export function packRowDescription(row, t) {
  return row.productId ? t(`${row.productId}Description`) : t(`${row.labelKey}Description`);
}
